import type { ReadingLevels } from "../../../shared/reading-levels";
import { ActivityHero } from "../../components/activity-hero/ActivityHero";
import { useEffect,useState,type FormEvent } from "react";
import type { Language,ReadingPreset } from "./types";
import { DIFFICULTIES,ENGLISH_LEVELS } from "./ui";
export type TopicFormProps = {
  onGenerate: (
    topic: string,
    language: Language,
    preset: ReadingPreset,
  ) => void;
  busy: boolean;
  language: Language;
  initialLevels?: ReadingLevels;
  levelsLoading?: boolean;
  onLevelChange?: (language: Language, preset: ReadingPreset) => Promise<boolean>;
};

export function TopicForm({
  onGenerate,
  busy,
  language,
  initialLevels = {},
  onLevelChange,
  levelsLoading = false,
}: TopicFormProps) {
  const [topic, setTopic] = useState("");
  const [difficulty, setDifficulty] = useState(() => Math.max(0, DIFFICULTIES.findIndex(option => option.value === (initialLevels.he ?? "medium"))));
  const [englishLevel, setEnglishLevel] = useState(() => Math.max(0, ENGLISH_LEVELS.findIndex(option => option.value === (initialLevels.en ?? "basic"))));
  useEffect(() => {
    if (levelsLoading) return;
    setDifficulty(Math.max(0, DIFFICULTIES.findIndex(option => option.value === (initialLevels.he ?? "medium"))));
    setEnglishLevel(Math.max(0, ENGLISH_LEVELS.findIndex(option => option.value === (initialLevels.en ?? "basic"))));
  }, [initialLevels.he, initialLevels.en, levelsLoading]);
  const selectedEnglishLevel = ENGLISH_LEVELS[englishLevel];

  async function selectLevel(index: number) {
    const options = language === "en" ? ENGLISH_LEVELS : DIFFICULTIES;
    if (onLevelChange && !await onLevelChange(language, options[index].value)) return;
    if (language === "en") setEnglishLevel(index);
    else setDifficulty(index);
  }

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (busy || levelsLoading) return;
    const clean = topic.trim();
    const preset = language === "en"
      ? selectedEnglishLevel.value
      : DIFFICULTIES[difficulty].value;
    if (clean) onGenerate(clean, language, preset);
  }

  return (
    <form className="topic-card activity-entry" onSubmit={submit}>
      <ActivityHero title="מה נקרא היום?" />

      <div className="topic-input">
        <label className="sr-only" htmlFor="topic">נושא הטקסט</label>
        <textarea
          id="topic"
          value={topic}
          onInput={(event) => setTopic(event.currentTarget.value)}
          placeholder="אפשר לבחור כל נושא שמסקרן אותך."
          maxLength={160}
          rows={2}
        />
      </div>

      {language === "en" ? (
        <div className="length-control" aria-busy={levelsLoading}>
          <span className="length-control__label" id="english-level-label">
            רמת האנגלית
          </span>
          <div
            className="length-options"
            role="radiogroup"
            aria-labelledby="english-level-label"
          >
            {ENGLISH_LEVELS.map(({ value, label }, index) => (
              <button
                className={`length-option ${!levelsLoading && index === englishLevel ? "is-selected" : ""}`}
                type="button"
                role="radio"
                aria-checked={!levelsLoading && index === englishLevel}
                key={value}
                disabled={busy || levelsLoading}
                onClick={() => void selectLevel(index)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      ) : (
        <div className="length-control" aria-busy={levelsLoading}>
          <span className="length-control__label" id="reading-level-label">
            אורך הטקסט
          </span>
          <div
            className="length-options"
            role="radiogroup"
            aria-labelledby="reading-level-label"
          >
            {DIFFICULTIES.map(({ value, label }, index) => (
              <button
                className={`length-option ${!levelsLoading && index === difficulty ? "is-selected" : ""}`}
                type="button"
                role="radio"
                aria-checked={!levelsLoading && index === difficulty}
                key={value}
                disabled={busy || levelsLoading}
                onClick={() => void selectLevel(index)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      )}

      <button
        className="button button--orange generate-button"
        disabled={!topic.trim() || busy || levelsLoading}
      >
        <span aria-hidden="true">✨</span> יוצרים טקסט
      </button>
    </form>
  );
}
